import styles from "./MonthlyRateDetailsRow.module.css";
import { useI18n } from "../../i18n";
import { PropsWithChildren, type JSX } from "react";

type MonthlyRateDetailsRowProps = {
  rowLabel: string;
  rowValue: number | string | undefined;
  rowDetail?: string;
  highlightDetails?: boolean;
};
const NONE = "-";

const MonthlyRateDetailsRow = ({
  rowLabel,
  rowValue,
  rowDetail,
  highlightDetails = false,
}: MonthlyRateDetailsRowProps): JSX.Element => {
  if (typeof rowValue === "string") {
    return (
      <MonthlyRateDetailsRowWithStringContent
        rowLabel={rowLabel}
        rowDetail={rowDetail}
        rowValue={rowValue}
        highlightDetails={highlightDetails}
      />
    );
  } else {
    return (
      <MonthlyRateDetailsRowWithNumberContent
        rowLabel={rowLabel}
        rowDetail={rowDetail}
        rowValue={rowValue}
        highlightDetails={highlightDetails}
      />
    );
  }
};

type MonthlyRateDetailsRowWithStringContentProps = {
  rowLabel: string;
  rowValue: string;
  rowDetail?: string;
  highlightDetails: boolean;
};

const MonthlyRateDetailsRowWithStringContent = ({
  rowLabel,
  rowValue,
  rowDetail,
  highlightDetails,
}: MonthlyRateDetailsRowWithStringContentProps): JSX.Element => {
  return (
    <MonthlyRateDetailsRowShell rowLabel={rowLabel}>
      <MonthlyRateDetailsRowBody
        highlightDetails={highlightDetails}
        rowValue={rowValue || NONE}
        rowDetail={rowDetail}
      />
    </MonthlyRateDetailsRowShell>
  );
};
type MonthlyRateDetailsRowWithNumberContentProps = {
  rowLabel: string;
  rowValue?: number;
  rowDetail?: string;
  highlightDetails: boolean;
};
const MonthlyRateDetailsRowWithNumberContent = ({
  rowLabel,
  rowValue,
  rowDetail,
  highlightDetails,
}: MonthlyRateDetailsRowWithNumberContentProps): JSX.Element => {
  const { locale, formatNumber } = useI18n();
  const rowValueOrNone = rowValue ?? NONE;
  if (rowValueOrNone === NONE) {
    return (
      <MonthlyRateDetailsRowShell rowLabel={rowLabel}>
        <span>{`${NONE} ${locale.currencySymbol}`}</span>
      </MonthlyRateDetailsRowShell>
    );
  } else {
    const numberFormattedAsCurrency = formatNumber(rowValueOrNone) + " " + locale.currencySymbol;
    return (
      <MonthlyRateDetailsRowShell rowLabel={rowLabel}>
        <MonthlyRateDetailsRowBody
          highlightDetails={highlightDetails}
          rowValue={numberFormattedAsCurrency}
          rowDetail={rowDetail}
        />
      </MonthlyRateDetailsRowShell>
    );
  }
};

interface MonthlyRateDetailsRowShellProps extends PropsWithChildren {
  rowLabel: string;
}

const MonthlyRateDetailsRowShell = (props: MonthlyRateDetailsRowShellProps) => {
  return (
    <tr className={styles.rowMonthlyRate}>
      <td>{props.rowLabel}</td>
      <td className={styles.rowMonthlyRateValue}>{props.children}</td>
    </tr>
  );
};

interface MonthlyRateDetailsRowBodyProps {
  highlightDetails: boolean;
  rowDetail?: string;
  rowValue: string;
}

const MonthlyRateDetailsRowBody = (props: MonthlyRateDetailsRowBodyProps) => {
  return props.highlightDetails ? (
    <MonthlyRateDetailsRowBodyContent content={`${props.rowValue} ${props.rowDetail ?? ""}`} />
  ) : (
    props.rowValue
  );
};

type MonthlyRateDetailsRowBodyContentProps = {
  content?: string;
};

const MonthlyRateDetailsRowBodyContent = (props: MonthlyRateDetailsRowBodyContentProps): JSX.Element => {
  const { content } = props;
  return <span className={styles.rowDetails}>{content}</span>;
};

export { MonthlyRateDetailsRow };
